import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Send, MoreVertical, Flag, Ban } from "lucide-react";
import api, { formatPrice, thumbUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export default function ChatView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    const { data } = await api.get(`/conversations/${id}`);
    if (mountedRef.current) setConv(data);
    return data;
  }, [id]);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    load()
      .catch(() => { if (mountedRef.current) toast.error("Chat niet gevonden"); })
      .finally(() => { if (mountedRef.current) setLoading(false); });
    const interval = setInterval(() => { load().catch(() => {}); }, 5000);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [id, load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conv?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${id}/messages`, { text });
      if (mountedRef.current) { setConv(data); setText(""); }
    } catch {
      toast.error("Bericht niet verzonden");
    } finally {
      if (mountedRef.current) setSending(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-2 border-[#333] border-t-[#FF6A00]" /></div>;
  }
  if (!conv) return null;

  return (
    <div className="mx-auto flex h-[calc(100vh-64px)] max-w-3xl flex-col px-0 md:h-[calc(100vh-72px)] md:px-8">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-[#333] bg-[#111] p-3">
        <button onClick={() => navigate("/chats")} data-testid="chat-back-btn" className="rounded-full p-1.5 text-[#F5F5F5] hover:bg-[#2A2A2A]">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <img src={conv.other_user?.avatar} alt="" className="h-10 w-10 rounded-full bg-[#2A2A2A]" />
        <div>
          <p className="font-semibold text-[#F5F5F5]">{conv.other_user?.name}</p>
          <p className="text-xs text-[#22C55E]">Online</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="chat-menu-btn" className="ml-auto rounded-full p-2 text-[#A0A0A0] hover:bg-[#2A2A2A] hover:text-[#F5F5F5]"><MoreVertical className="h-5 w-5" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]">
            <DropdownMenuItem data-testid="chat-report" onClick={async () => { await api.post("/report", { target_type: "user", target_id: conv.other_user.id, reason: "Chat melding" }); toast.success("Melding ontvangen"); }} className="cursor-pointer focus:bg-[#2A2A2A]"><Flag className="mr-2 h-4 w-4" /> Gebruiker melden</DropdownMenuItem>
            <DropdownMenuItem data-testid="chat-block" onClick={async () => { await api.post(`/block/${conv.other_user.id}`); toast.success("Gebruiker geblokkeerd"); }} className="cursor-pointer text-[#EF4444] focus:bg-[#2A2A2A]"><Ban className="mr-2 h-4 w-4" /> Blokkeren</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Product context */}
      {conv.listing && (
        <Link to={`/advertentie/${conv.listing.id}`} data-testid="chat-listing-context" className="flex items-center gap-3 border-b border-[#333] bg-[#1c1c1c] p-3 hover:bg-[#222]">
          <img src={thumbUrl(conv.listing.image, 120)} alt="" loading="lazy" decoding="async" className="h-12 w-12 rounded-lg bg-[#2A2A2A] object-cover" />
          <div className="flex-1">
            <p className="font-semibold text-[#F5F5F5]">{conv.listing.title}</p>
            <p className="font-display text-[#FF6A00]">{formatPrice(conv.listing.price)}</p>
          </div>
        </Link>
      )}

      {/* Messages */}
      <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
        {conv.messages.length === 0 && (
          <p className="mt-8 text-center text-sm text-[#777]">Stuur een bericht om het gesprek te starten.</p>
        )}
        {conv.messages.map((m) => {
          if (m.system) {
            return (
              <div key={m.id} className="flex justify-center" data-testid="chat-system-message">
                <div className="max-w-[85%] rounded-xl border border-[#FF6A00]/40 bg-[#FF6A00]/10 px-4 py-2 text-center">
                  <p className="whitespace-pre-line break-words text-sm font-medium text-[#FFB27A]">{m.text}</p>
                  <p className="mt-0.5 text-[10px] text-[#888]">{time(m.created_at)}</p>
                </div>
              </div>
            );
          }
          const own = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${own ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${own ? "bg-[#FF6A00] text-[#111]" : "bg-[#2A2A2A] text-[#F5F5F5]"}`}>
                <p className="whitespace-pre-line break-words text-sm">{m.text}</p>
                <p className={`mt-0.5 text-[10px] ${own ? "text-[#111]/60" : "text-[#888]"}`}>{time(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <form onSubmit={send} className="flex items-center gap-2 border-t border-[#333] bg-[#111] p-3 pb-[76px] md:pb-3">
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Typ een bericht..."
          className="flex-1 rounded-full border border-[#333] bg-[#2A2A2A] px-4 py-3 text-[#F5F5F5] outline-none placeholder:text-[#777] focus:border-[#FF6A00]"
        />
        <button type="submit" disabled={sending} data-testid="chat-send-btn" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#FF6A00] text-[#111] transition-transform active:scale-95 disabled:opacity-60">
          <Send className="h-5 w-5" />
        </button>
      </form>
    </div>
  );
}

function time(iso) {
  return new Date(iso).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
}
